import React, { useState, useEffect, useRef } from 'react';

const CASTILIAN_TO_CATALAN = [
  ['Ignacio', 'Ignasi'], ['José', 'Josep'], ['Jose', 'Josep'], ['Juan', 'Joan'], ['Vicente', 'Vicent'],
  ['Bartolomé', 'Bartomeu'], ['Bartolome', 'Bartomeu'], ['Pedro', 'Pere'], ['Antonio', 'Antoni'],
  ['Francisco', 'Francesc'], ['Miguel', 'Miquel'], ['Jaime', 'Jaume'], ['Santa Eulalia', 'Santa Eulària'],
  ['San Antonio', 'Sant Antoni'], ['San José', 'Sant Josep'], ['San Juan', 'Sant Joan'], ['San Rafael', 'Sant Rafel'],
];
import { MapContainer, TileLayer, Marker, useMap, useMapEvents } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { MapPin, Navigation, Search, Loader2 } from 'lucide-react';

// Fix Leaflet default icon
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png',
});

const IBIZA_CENTER = [38.9067, 1.4206];

function MapClickHandler({ onLocationSelect }) {
  useMapEvents({
    click(e) {
      onLocationSelect(e.latlng.lat, e.latlng.lng);
    },
  });
  return null;
}

function FlyToMarker({ position }) {
  const map = useMap();
  useEffect(() => {
    if (position) map.flyTo(position, 15);
  }, [position, map]);
  return null;
}

const LocationPicker = ({ latitude, longitude, onLocationChange, onDistanceCalculated, onAddressFound }) => {
  const [position, setPosition] = useState(latitude && longitude ? [latitude, longitude] : null);
  const [searchQuery, setSearchQuery] = useState('');
  const [searching, setSearching] = useState(false);
  const [results, setResults] = useState([]);
  const [gettingGPS, setGettingGPS] = useState(false);
  const [distanceInfo, setDistanceInfo] = useState(null);
  const [distError, setDistError] = useState('');
  const API_URL = process.env.REACT_APP_BACKEND_URL;

  const reverseGeocode = async (lat, lng) => {
    try {
      const r = await fetch(`https://nominatim.openstreetmap.org/reverse?format=json&lat=${lat}&lon=${lng}&zoom=18`);
      const j = await r.json();
      if (j.display_name) onAddressFound(j.display_name);
    } catch { /* patient can type the address manually */ }
  };

  const handleLocationSelect = async (lat, lng, label) => {
    setPosition([lat, lng]);
    onLocationChange(lat, lng);
    if (label) onAddressFound(label); else reverseGeocode(lat, lng);
    await calculateDistance(lat, lng);
  };

  const calculateDistance = async (lat, lng) => {
    setDistError('');
    try {
      const res = await fetch(`${API_URL}/api/distance/calculate?lat=${lat}&lng=${lng}`);
      const data = await res.json();
      setDistanceInfo(data);
      onDistanceCalculated(data.distance_km, data.supplement);
    } catch (e) {
      setDistError('Could not calculate distance');
    }
  };

  const handleGPS = () => {
    if (!navigator.geolocation) {
      setDistError('Geolocation not supported');
      return;
    }
    setGettingGPS(true);
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        handleLocationSelect(pos.coords.latitude, pos.coords.longitude);
        setGettingGPS(false);
      },
      (err) => {
        setDistError('Location access denied. Please drop a pin on the map.');
        setGettingGPS(false);
      },
      { enableHighAccuracy: true, timeout: 10000 }
    );
  };

  const handleSearch = async (e) => {
    e.preventDefault();
    if (!searchQuery.trim()) return;
    setSearching(true);
    setDistError('');
    try {
      const viewbox = '1.15,38.82,1.65,39.12';
      const query = async (text, bounded) => {
        const r = await fetch(`https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(text)}&viewbox=${viewbox}&bounded=${bounded}&countrycodes=es&limit=5`);
        return r.json();
      };
      const photon = async (text) => {
        const r = await fetch(`https://photon.komoot.io/api/?q=${encodeURIComponent(text + ' Ibiza')}&lat=38.91&lon=1.43&limit=5`);
        const j = await r.json();
        return j.features.map(f => ({ place_id: `ph-${f.properties.osm_id}`, lat: f.geometry.coordinates[1], lon: f.geometry.coordinates[0],
          display_name: [f.properties.name, f.properties.street, f.properties.city || f.properties.county].filter(Boolean).join(', ') }));
      };
      const raw = searchQuery.trim();
      const stripped = raw.replace(/^(calle|c\/|avenida|avda\.?|av\.?|paseo|plaza|carrer|avinguda|passeig|placa|plaça)\s+(de\s+(la\s+|los\s+|las\s+)?|del\s+|d')?/i, '');
      const catalan = CASTILIAN_TO_CATALAN.reduce((s, [es, ca]) => s.replace(new RegExp(`\\b${es}\\b`, 'gi'), ca), stripped);
      let data = await query(raw, 1);
      if (!data.length && catalan !== raw) data = await query(catalan, 1);
      if (!data.length && stripped !== raw) data = await query(stripped, 1);
      if (!data.length) data = await query(raw, 0);
      if (!data.length) data = await photon(stripped);
      if (data.length > 0) {
        setResults(data.length > 1 ? data : []);
        handleLocationSelect(parseFloat(data[0].lat), parseFloat(data[0].lon), data[0].display_name);
      } else {
        setResults([]);
        setDistError("Can't find it? Tap the map to drop a pin at your exact location.");
      }
    } catch (e) {
      setDistError("Search failed. Tap the map to drop a pin at your exact location.");
    }
    setSearching(false);
  };

  return (
    <div className="space-y-3" data-testid="location-picker">
      <label className="block text-sm font-medium text-[#0f172a]">Set Your Exact Location *</label>
      <p className="text-xs text-[#64748b]">Use GPS, search for an address, or tap the map to drop a pin.</p>

      <div className="flex gap-2">
        <button type="button" onClick={handleGPS} disabled={gettingGPS}
          className="flex items-center gap-1.5 px-3 py-2 bg-[#0f172a] text-white text-sm hover:bg-[#b8860b] transition-colors disabled:opacity-50"
          data-testid="btn-gps">
          {gettingGPS ? <Loader2 size={14} className="animate-spin" /> : <Navigation size={14} />}
          Use my current location
        </button>
      </div>

      <form onSubmit={handleSearch} className="flex gap-2">
        <div className="flex-1 relative">
          <input type="text" value={searchQuery} onChange={e => setSearchQuery(e.target.value)}
            className="w-full p-2 pl-9 border border-[#e2e8f0] text-sm focus:outline-none focus:border-[#b8860b]"
            placeholder="Search address in Ibiza..." data-testid="location-search" />
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#94a3b8]" />
        </div>
        <button type="submit" disabled={searching} data-testid="location-search-btn" className="px-3 py-2 bg-[#f1f5f9] border border-[#e2e8f0] text-sm hover:bg-[#e2e8f0] transition-colors disabled:opacity-50">
          {searching ? <Loader2 size={14} className="animate-spin" /> : 'Search'}
        </button>
      </form>

      {results.length > 1 && (
        <ul className="border border-[#e2e8f0] bg-white text-sm divide-y divide-[#e2e8f0]" data-testid="location-results">
          {results.map((r, i) => (
            <li key={r.place_id}>
              <button type="button" data-testid={`location-result-${i}`}
                onClick={() => { handleLocationSelect(parseFloat(r.lat), parseFloat(r.lon), r.display_name); setResults([]); }}
                className="w-full text-left px-3 py-2 hover:bg-[#f8fafc] transition-colors">{r.display_name}</button>
            </li>
          ))}
        </ul>
      )}

      <div className="border border-[#e2e8f0] overflow-hidden" style={{ height: 280 }}>
        <MapContainer center={position || IBIZA_CENTER} zoom={position ? 15 : 12} style={{ height: '100%', width: '100%' }} zoomControl={true}>
          <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>' />
          <MapClickHandler onLocationSelect={handleLocationSelect} />
          {position && (
            <>
              <Marker position={position} />
              <FlyToMarker position={position} />
            </>
          )}
        </MapContainer>
      </div>

      {position && (
        <p className="text-xs text-[#22c55e] flex items-center gap-1" data-testid="location-set">
          <MapPin size={12} /> Location set: {position[0].toFixed(5)}, {position[1].toFixed(5)}
        </p>
      )}

      {distanceInfo && distanceInfo.distance_km > 0 && (
        <div data-testid="distance-info" className={`text-sm p-3 border ${distanceInfo.supplement > 0 ? 'bg-[#fffbeb] border-[#fde68a] text-[#92400e]' : 'bg-[#f0fdf4] border-[#bbf7d0] text-[#166534]'}`}>
          <strong>Distance:</strong> {distanceInfo.distance_km} km from base
          {distanceInfo.supplement > 0 && (
            <span> — <strong>€{distanceInfo.supplement} distance supplement</strong> applies (over {distanceInfo.threshold_km} km)</span>
          )}
        </div>
      )}

      {distError && <p className="text-xs text-red-500" data-testid="location-error">{distError}</p>}
    </div>
  );
};

export default LocationPicker;
